import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Stat, StatusBadge, Textarea } from "@/components/UI";
import { Plus, Trash, Coin, Receipt, CheckCircle, Warning, PencilSimple, Clock, UserCircle, Calendar as CalendarIcon, ChartPieSlice, Leaf, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATS, PARTY_TYPES, PAYMENT_TYPES, LEAVE_REASONS, MONTHS_TR, today, fmt } from "./constants";

function emptyPersonnel() {
  return { first_name: "", last_name: "", title: "", monthly_salary: 0, monthly_insurance: 0,
           start_date: today(), annual_leave_days: 14, phone: "", notes: "", active: true };
}

export function PersonnelTab() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null); // detail view
  const [editing, setEditing] = useState(null);   // null | 'new' | id
  const [form, setForm] = useState(emptyPersonnel());

  const load = async () => {
    const [l, s] = await Promise.all([api.get("/personnel"), api.get("/personnel/summary")]);
    setList(l.data);
    setSummary(s.data);
    if (selected) {
      const upd = l.data.find(x => x.id === selected.id);
      if (upd) setSelected(upd);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      monthly_salary: Number(form.monthly_salary || 0),
      monthly_insurance: Number(form.monthly_insurance || 0),
      annual_leave_days: Number(form.annual_leave_days || 14),
    };
    try {
      if (editing === "new") {
        await api.post("/personnel", body);
        toast.success("Personel eklendi");
      } else {
        await api.patch(`/personnel/${editing}`, body);
        toast.success("Güncellendi");
      }
      setEditing(null); setForm(emptyPersonnel()); load();
    } catch (err) { toast.error(err?.message || "Hata"); }
  };

  const edit = (p) => { setForm({ ...p }); setEditing(p.id); };
  const remove = async (p) => {
    if (!window.confirm(`${p.first_name} ${p.last_name} silinsin mi?`)) return;
    try { await api.delete(`/personnel/${p.id}`); toast.success("Silindi"); load(); if (selected?.id === p.id) setSelected(null); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-6" data-testid="personnel-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c]">Aktif Personel</div>
            <div className="text-xl font-bold tabnum text-[#1A1D1A]">{summary.active_count} <span className="text-sm font-normal text-[#6b746c]">kişi</span></div>
          </Card>
          <Card className="!bg-[#fbeee9] border border-[#f2d4cb]">
            <div className="text-xs uppercase tracking-widest text-[#8a3a2b]">Aylık Bordro</div>
            <div className="text-xl font-bold tabnum text-[#8a3a2b]">{summary.monthly_payroll.toLocaleString("tr-TR")} ₺</div>
          </Card>
          <Card className="!bg-[#F5E9D3] border border-[#e0c890]">
            <div className="text-xs uppercase tracking-widest text-[#8B6914]">Aylık Sigorta</div>
            <div className="text-xl font-bold tabnum text-[#8B6914]">{summary.monthly_insurance.toLocaleString("tr-TR")} ₺</div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c]">Aylık Toplam Maliyet</div>
            <div className="text-xl font-bold tabnum text-[#1A1D1A]">{summary.total_monthly_cost.toLocaleString("tr-TR")} ₺</div>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button data-testid="personnel-new-btn" onClick={() => { setForm(emptyPersonnel()); setEditing("new"); setSelected(null); }}>
          <Plus size={14} weight="bold" className="inline mr-1" /> Yeni Personel
        </Button>
      </div>

      {editing && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading text-lg">{editing === "new" ? "Yeni Personel" : "Personel Düzenle"}</h3>
            <Button type="button" onClick={() => setEditing(null)} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
          </div>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-3">
            <Field label="Ad *"><Input data-testid="pers-first-name" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Soyad *"><Input data-testid="pers-last-name" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Ünvan / Görev"><Input data-testid="pers-title" value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Örn: Sağımcı, Yem Operatörü, Veteriner Yrd" /></Field>
            <Field label="Aylık Maaş (₺) *"><Input data-testid="pers-salary" type="number" step="0.01" min="0" required value={form.monthly_salary} onChange={e => setForm({ ...form, monthly_salary: e.target.value })} /></Field>
            <Field label="Aylık Sigorta / SGK (₺)"><Input data-testid="pers-insurance" type="number" step="0.01" min="0" value={form.monthly_insurance} onChange={e => setForm({ ...form, monthly_insurance: e.target.value })} /></Field>
            <Field label="İşe Başlama"><Input data-testid="pers-start-date" type="date" value={form.start_date || ""} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Yıllık İzin Hakkı (gün)"><Input data-testid="pers-leave" type="number" min="0" max="60" value={form.annual_leave_days} onChange={e => setForm({ ...form, annual_leave_days: e.target.value })} /></Field>
            <Field label="Telefon"><Input data-testid="pers-phone" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="0555 111 22 33" /></Field>
            <Field label="Durum">
              <Select data-testid="pers-active" value={form.active ? "aktif" : "pasif"} onChange={e => setForm({ ...form, active: e.target.value === "aktif" })}>
                <option value="aktif">Aktif</option>
                <option value="pasif">Pasif (İşten Ayrılmış)</option>
              </Select>
            </Field>
            <div className="md:col-span-3">
              <Field label="Notlar"><Textarea rows={2} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" data-testid="pers-save"><Plus size={14} className="inline mr-1" weight="bold" /> Kaydet</Button>
            </div>
          </form>
        </Card>
      )}

      {!editing && !selected && (
        <PersonnelList list={list} onSelect={setSelected} onEdit={edit} onDelete={remove} />
      )}

      {selected && !editing && (
        <PersonnelDetail person={selected} onBack={() => setSelected(null)} onEdit={() => edit(selected)} onChanged={load} />
      )}
    </div>
  );
}

function PersonnelList({ list, onSelect, onEdit, onDelete }) {
  if (!list.length) {
    return (
      <Card className="text-center py-12">
        <UserCircle size={40} weight="duotone" className="mx-auto text-[#8a938c] mb-3" />
        <div className="text-[#6b746c] mb-1">Henüz personel yok</div>
        <div className="text-xs text-[#8a938c]">"Yeni Personel" ile başlayın.</div>
      </Card>
    );
  }
  return (
    <Card className="!p-0 overflow-hidden">
      <table className="data-table">
        <thead><tr>
          <th>Ad Soyad</th><th>Ünvan</th>
          <th className="text-right">Maaş</th>
          <th className="text-right">Sigorta</th>
          <th className="text-right">Bu Yıl Ödenen</th>
          <th className="text-right">Kalan İzin</th>
          <th>Durum</th><th></th>
        </tr></thead>
        <tbody>
          {list.map(p => (
            <tr key={p.id} data-testid={`pers-row-${p.id}`} className={p.active ? "" : "opacity-60"}>
              <td>
                <button onClick={() => onSelect(p)} className="font-semibold text-left hover:underline">
                  {p.first_name} {p.last_name}
                </button>
                {p.phone && <div className="text-xs text-[#8a938c] mt-0.5">{p.phone}</div>}
              </td>
              <td className="text-[#6b746c]">{p.title || "—"}</td>
              <td className="text-right tabnum">{Number(p.monthly_salary || 0).toLocaleString("tr-TR")} ₺</td>
              <td className="text-right tabnum text-[#8B6914]">{Number(p.monthly_insurance || 0).toLocaleString("tr-TR")} ₺</td>
              <td className="text-right tabnum">{Number(p.paid_this_year || 0).toLocaleString("tr-TR")} ₺</td>
              <td className={`text-right tabnum ${p.leave_remaining_this_year <= 2 ? "text-[#C45B45] font-semibold" : ""}`}>
                {p.leave_remaining_this_year} / {p.annual_leave_days}
              </td>
              <td>
                <span className={`text-xs px-2 py-0.5 rounded ${p.active ? "bg-[#E4EEE6] text-[#2D4A36]" : "bg-[#F5F2EC] text-[#6b746c]"}`}>
                  {p.active ? "aktif" : "pasif"}
                </span>
              </td>
              <td>
                <div className="flex gap-1 justify-end">
                  <button onClick={() => onSelect(p)} data-testid={`pers-detail-${p.id}`}
                    className="p-1.5 rounded hover:bg-[#F5F2EC]" title="Detay & Ödemeler">
                    <Coin size={16} />
                  </button>
                  <button onClick={() => onEdit(p)} data-testid={`pers-edit-${p.id}`}
                    className="p-1.5 rounded hover:bg-[#F5F2EC]" title="Düzenle">
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => onDelete(p)} data-testid={`pers-del-${p.id}`}
                    className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Sil">
                    <Trash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PersonnelDetail({ person, onBack, onEdit, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [payForm, setPayForm] = useState({ period_year: new Date().getFullYear(), period_month: new Date().getMonth() + 1, payment_type: "maaş", amount: person.monthly_salary || 0, payment_date: today(), notes: "" });
  const [leaveForm, setLeaveForm] = useState({ start_date: today(), end_date: today(), reason: "yıllık izin", notes: "" });
  const [showPayForm, setShowPayForm] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  const load = async () => {
    const r = await api.get(`/personnel/${person.id}`);
    setDetail(r.data);
  };
  useEffect(() => { load(); }, [person.id]); // eslint-disable-line

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/personnel/${person.id}/payments`, { ...payForm, amount: Number(payForm.amount), period_year: Number(payForm.period_year), period_month: Number(payForm.period_month) });
      toast.success("Ödeme kaydedildi · Finans'a otomatik gider eklendi");
      setShowPayForm(false); load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const delPayment = async (id) => {
    if (!window.confirm("Bu ödemeyi silmek istediğinize emin misiniz? Bağlı Finans gideri de silinecek.")) return;
    try { await api.delete(`/personnel/payments/${id}`); toast.success("Silindi"); load(); onChanged(); }
    catch (err) { toast.error(err.message); }
  };
  const addLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/personnel/${person.id}/leaves`, leaveForm);
      toast.success("İzin kaydedildi");
      setShowLeaveForm(false); load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const delLeave = async (id) => {
    if (!window.confirm("Bu izin kaydı silinsin mi?")) return;
    try { await api.delete(`/personnel/leaves/${id}`); toast.success("Silindi"); load(); onChanged(); }
    catch (err) { toast.error(err.message); }
  };

  const leaveDaysPreview = (() => {
    try {
      const s = new Date(leaveForm.start_date); const e = new Date(leaveForm.end_date);
      const d = Math.round((e - s) / 86400000) + 1;
      return d > 0 ? d : 0;
    } catch { return 0; }
  })();

  if (!detail) return <Card>Yükleniyor...</Card>;

  return (
    <div className="space-y-4" data-testid={`pers-detail-view-${person.id}`}>
      <Card>
        <div className="flex items-start justify-between">
          <div>
            <button onClick={onBack} className="text-xs text-[#6b746c] hover:underline mb-2">← Personel listesine dön</button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-[#2D4A36] text-white flex items-center justify-center font-bold text-lg">
                {(detail.first_name?.[0] || "") + (detail.last_name?.[0] || "")}
              </div>
              <div>
                <h2 className="heading text-2xl">{detail.first_name} {detail.last_name}</h2>
                <div className="text-sm text-[#6b746c]">
                  {detail.title || "Ünvan yok"}{detail.start_date ? ` · ${detail.start_date} tarihinden beri` : ""}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
              <div><span className="text-[#6b746c]">Aylık Maaş:</span> <b className="tabnum">{Number(detail.monthly_salary || 0).toLocaleString("tr-TR")} ₺</b></div>
              <div><span className="text-[#6b746c]">Aylık Sigorta:</span> <b className="tabnum text-[#8B6914]">{Number(detail.monthly_insurance || 0).toLocaleString("tr-TR")} ₺</b></div>
              <div><span className="text-[#6b746c]">Yıllık İzin:</span> <b className="tabnum">{detail.annual_leave_days} gün</b></div>
              <div><span className="text-[#6b746c]">Telefon:</span> <b>{detail.phone || "—"}</b></div>
            </div>
          </div>
          <Button onClick={onEdit} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">
            <PencilSimple size={14} className="inline mr-1" weight="duotone" /> Düzenle
          </Button>
        </div>
      </Card>

      {/* Payments Section */}
      <Card className="!p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coin size={18} weight="duotone" />
            <h3 className="heading text-lg">Maaş & Ödemeler</h3>
            <span className="text-xs text-[#6b746c]">({detail.payments.length} kayıt)</span>
          </div>
          <Button onClick={() => setShowPayForm(!showPayForm)} data-testid="pers-add-payment-btn">
            <Plus size={14} className="inline mr-1" weight="bold" /> Ödeme Ekle
          </Button>
        </div>
        {showPayForm && (
          <div className="p-4 bg-[#F5F2EC] border-b border-[#E5E2DC]">
            <form onSubmit={addPayment} className="grid md:grid-cols-4 gap-3">
              <Field label="Dönem — Ay *">
                <Select data-testid="pay-month" value={payForm.period_month} onChange={e => setPayForm({ ...payForm, period_month: e.target.value })}>
                  {MONTHS_TR.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </Select>
              </Field>
              <Field label="Yıl *"><Input data-testid="pay-year" type="number" required value={payForm.period_year} onChange={e => setPayForm({ ...payForm, period_year: e.target.value })} /></Field>
              <Field label="Tip *">
                <Select data-testid="pay-type" value={payForm.payment_type} onChange={e => setPayForm({ ...payForm, payment_type: e.target.value })}>
                  {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Tutar (₺) *"><Input data-testid="pay-amount" type="number" step="0.01" required value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} /></Field>
              <Field label="Ödeme Tarihi *"><Input data-testid="pay-date" type="date" required value={payForm.payment_date} onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })} /></Field>
              <div className="md:col-span-3">
                <Field label="Not"><Input value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} /></Field>
              </div>
              <div className="md:col-span-4 flex gap-2">
                <Button type="submit" data-testid="pay-save">Kaydet</Button>
                <Button type="button" onClick={() => setShowPayForm(false)} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
              </div>
            </form>
          </div>
        )}
        <table className="data-table">
          <thead><tr>
            <th>Dönem</th><th>Tip</th>
            <th className="text-right">Tutar</th>
            <th>Ödeme Tarihi</th>
            <th>Not</th><th></th>
          </tr></thead>
          <tbody>
            {detail.payments.map(p => (
              <tr key={p.id} data-testid={`pay-row-${p.id}`}>
                <td className="font-semibold">{MONTHS_TR[p.period_month - 1]} {p.period_year}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                    p.payment_type === "maaş" ? "bg-[#E4EEE6] text-[#2D4A36]"
                    : p.payment_type === "sigorta" ? "bg-[#F5E9D3] text-[#8B6914]"
                    : "bg-[#F5F2EC] text-[#6b746c]"
                  }`}>{p.payment_type}</span>
                </td>
                <td className="text-right tabnum font-semibold text-[#C45B45]">− {Number(p.amount).toLocaleString("tr-TR")} ₺</td>
                <td className="tabnum">{p.payment_date}</td>
                <td className="text-xs text-[#6b746c]">{p.notes || "—"}</td>
                <td><button onClick={() => delPayment(p.id)} data-testid={`pay-del-${p.id}`} className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]"><Trash size={14} /></button></td>
              </tr>
            ))}
            {!detail.payments.length && <tr><td colSpan={6} className="text-center py-6 text-[#8a938c]">Henüz ödeme yok</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Leaves Section */}
      <Card className="!p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon size={18} weight="duotone" />
            <h3 className="heading text-lg">Yıllık İzin & Devamsızlık</h3>
            <span className="text-xs text-[#6b746c]">
              (bu yıl: {person.leave_used_this_year}/{person.annual_leave_days} gün · kalan: <b className={person.leave_remaining_this_year <= 2 ? "text-[#C45B45]" : "text-[#2D4A36]"}>{person.leave_remaining_this_year}</b>)
            </span>
          </div>
          <Button onClick={() => setShowLeaveForm(!showLeaveForm)} data-testid="pers-add-leave-btn">
            <Plus size={14} className="inline mr-1" weight="bold" /> İzin Ekle
          </Button>
        </div>
        {showLeaveForm && (
          <div className="p-4 bg-[#F5F2EC] border-b border-[#E5E2DC]">
            <form onSubmit={addLeave} className="grid md:grid-cols-4 gap-3">
              <Field label="Başlangıç *"><Input data-testid="leave-start" type="date" required value={leaveForm.start_date} onChange={e => setLeaveForm({ ...leaveForm, start_date: e.target.value })} /></Field>
              <Field label="Bitiş *"><Input data-testid="leave-end" type="date" required value={leaveForm.end_date} onChange={e => setLeaveForm({ ...leaveForm, end_date: e.target.value })} /></Field>
              <Field label="Sebep *">
                <Select data-testid="leave-reason" value={leaveForm.reason} onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })}>
                  {LEAVE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
              <div className="text-sm flex items-end pb-2">
                <span className="text-[#6b746c]">Toplam:&nbsp;</span>
                <b className="tabnum">{leaveDaysPreview} gün</b>
              </div>
              <div className="md:col-span-4">
                <Field label="Not"><Input value={leaveForm.notes} onChange={e => setLeaveForm({ ...leaveForm, notes: e.target.value })} /></Field>
              </div>
              <div className="md:col-span-4 flex gap-2">
                <Button type="submit" data-testid="leave-save">Kaydet</Button>
                <Button type="button" onClick={() => setShowLeaveForm(false)} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
              </div>
            </form>
          </div>
        )}
        <table className="data-table">
          <thead><tr><th>Tarih</th><th className="text-right">Gün</th><th>Sebep</th><th>Not</th><th></th></tr></thead>
          <tbody>
            {detail.leaves.map(l => (
              <tr key={l.id} data-testid={`leave-row-${l.id}`}>
                <td className="tabnum text-sm">{l.start_date} → {l.end_date}</td>
                <td className="text-right tabnum font-semibold">{l.days}</td>
                <td className="capitalize">{l.reason}</td>
                <td className="text-xs text-[#6b746c]">{l.notes || "—"}</td>
                <td><button onClick={() => delLeave(l.id)} data-testid={`leave-del-${l.id}`} className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]"><Trash size={14} /></button></td>
              </tr>
            ))}
            {!detail.leaves.length && <tr><td colSpan={5} className="text-center py-6 text-[#8a938c]">Henüz izin kaydı yok</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// -------- FEED ANALYSIS TAB (Yem Gideri Ayrımı & Tarla Kârı) --------
